import {Component, computed, effect, inject, OnDestroy, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoDirective} from '@jsverse/transloco';
import {ProjectUserListComponent} from './project-user-list/project-user-list.component';
import {UserInvitationTemplateListComponent} from './user-invitation-template-list/user-invitation-template-list.component';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {ProjectUserService} from './project-user.service';
import {ActivatedRoute} from '@angular/router';
import {ProjectDetailResponse} from '@frontend/shared-api';
import {Subject, takeUntil} from 'rxjs';
import {UserInvitationTemplateDialogService} from './user-invitation-template-dialog/user-invitation-template-dialog.service';
import {TabsUnsavedRevealDirective} from '../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-project-user-management',
  imports: [
    CommonModule,
    TranslocoDirective,
    ProjectUserListComponent,
    UserInvitationTemplateListComponent,
    Tab,
    TabList,
    Tabs,
    TabPanels,
    TabPanel,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './project-user-management.component.html',
})
export class ProjectUserManagementComponent implements OnDestroy {
  private readonly projectUserService = inject(ProjectUserService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(UserInvitationTemplateDialogService);
  private readonly destroy$ = new Subject<void>();

  project = this.route.snapshot.data['project'] as ProjectDetailResponse;
  tabItems = ['userList', 'invitationTemplates'];
  activeTabIndex = signal(this.tabItems[0]);

  private readonly templatesLoaded = signal(false);
  private readonly customTemplatesLoaded = signal(false);
  private readonly allTemplatesReady = computed(
    () =>
      this.templatesLoaded() &&
      this.customTemplatesLoaded() &&
      this.projectUserService.notSavedOriginalTemplates().size > 0,
  );

  constructor() {
    this.projectUserService.loadRoles();
    this.loadTemplatesForAllRoles();
    this.loadCustomTemplates();

    this.dialogService.templateSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadCustomTemplates();
    });

    effect(() => {
      if (this.allTemplatesReady()) {
        this.saveAllOriginalTemplates();
      }
    });
  }

  private loadTemplatesForAllRoles(): void {
    effect(() => {
      const roles = this.projectUserService.rolesList();
      if (roles.length > 0 && !this.templatesLoaded()) {
        this.projectUserService.fetchTemplatesForRoles(roles).subscribe({
          next: () => this.templatesLoaded.set(true),
        });
      }
    });
  }

  private saveAllOriginalTemplates(): void {
    this.projectUserService.saveAllOriginalTemplates(this.project.id).subscribe();
  }

  private loadCustomTemplates(): void {
    this.projectUserService.loadCustomTemplates(this.project.id).subscribe({
      next: () => this.customTemplatesLoaded.set(true),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
